/**
 * <cart-drawer-recommendations> - dynamic product recommendations for the cart drawer.
 *
 * Why this exists instead of Prestige's own <product-recommendations>:
 * that element derives the section to re-render from `this.closest('.shopify-section')`
 * and passes it through extractSectionId(). The cart drawer is MOVED to <body> when it
 * opens - verified on this store: while closed its parent chain is
 * [section#shopify-section-sections--...__cart-drawer, body, html], and once open it is
 * [body, html] with the original section wrapper left behind empty. So from the first
 * open onwards that ancestor no longer exists, extractSectionId(null) throws on
 * `null.classList` before any fetch is made, and because the native element sets its
 * `_isLoaded` guard BEFORE fetching, the instance never retries. CartDrawer#replaceContent
 * rebuilds the drawer on every cart:change, so every removal or in-page add produced a
 * fresh element that threw instantly and stayed empty - the block disappeared and never
 * came back. Page load worked only because the drawer was still inside its wrapper then.
 *
 * This element is told its section id explicitly, so it has no DOM-position dependency
 * and survives being re-rendered inside a relocated drawer.
 */
(() => {
  if (window.customElements.get('cart-drawer-recommendations')) {
    return;
  }

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      // Each cart:change re-render produces a brand new element, so a per-instance
      // flag is all that is needed to avoid double-fetching.
      if (this.hasLoaded) {
        return;
      }

      this.hasLoaded = true;
      this.load();
    }

    disconnectedCallback() {
      // The drawer is replaced wholesale on cart:change; drop any in-flight request
      // so a stale response can never be applied.
      this.abortController?.abort();
    }

    async load() {
      const sectionId = this.getAttribute('section-id');
      const productId = this.getAttribute('product');

      if (!sectionId || !productId) {
        this.remove();
        return;
      }

      const params = new URLSearchParams({
        product_id: productId,
        limit: this.getAttribute('limit') || '4',
        section_id: sectionId,
        intent: this.getAttribute('intent') || 'related',
      });

      const root = window.Shopify?.routes?.root || '/';
      this.abortController = new AbortController();

      try {
        const response = await fetch(`${root}recommendations/products?${params}`, {
          priority: 'low',
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          this.remove();
          return;
        }

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('cart-drawer-recommendations');

        // No usable products (everything already in the cart, or none returned and no
        // hand-picked fallback set) => remove, so the block is hidden rather than left
        // as a bare heading.
        if (!incoming || incoming.childElementCount === 0) {
          this.remove();
          return;
        }

        this.replaceChildren(...document.importNode(incoming, true).childNodes);
        this.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.remove();
        }
      }
    }
  }

  window.customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
})();
